import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useIsAuthenticated } from '@azure/msal-react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from './store'
import { loginSuccess, logout } from './store/slices/authSlice'
import { api } from './services/api'
import Layout from './components/layout/Layout'
import LoadingSpinner from './components/common/LoadingSpinner'

const LandingPage        = React.lazy(() => import('./pages/LandingPage'))
const LoginPage          = React.lazy(() => import('./pages/LoginPage'))
const RegisterPage       = React.lazy(() => import('./pages/RegisterPage'))
const MfaPage            = React.lazy(() => import('./pages/MfaPage'))
const DashboardPage      = React.lazy(() => import('./pages/DashboardPage'))
const TrackingPage       = React.lazy(() => import('./pages/TrackingPage'))
const AlertsPage         = React.lazy(() => import('./pages/AlertsPage'))
const CircuitsPage       = React.lazy(() => import('./pages/CircuitsPage'))
const StudentsPage       = React.lazy(() => import('./pages/StudentsPage'))
const AdminUsersPage     = React.lazy(() => import('./pages/admin/AdminUsersPage'))
const AdminRolesPage     = React.lazy(() => import('./pages/admin/AdminRolesPage'))
const AdminSettingsPage  = React.lazy(() => import('./pages/admin/AdminSettingsPage'))
const AdminImportPage    = React.lazy(() => import('./pages/admin/AdminImportPage'))
const SchoolClassesPage  = React.lazy(() => import('./pages/admin/SchoolClassesPage'))
const SafetyZonesPage    = React.lazy(() => import('./pages/admin/SafetyZonesPage'))
const ServiceZonesPage   = React.lazy(() => import('./pages/admin/ServiceZonesPage'))
const AdminAbsencesPage  = React.lazy(() => import('./pages/admin/AdminAbsencesPage'))
const GpsModulesPage     = React.lazy(() => import('./pages/admin/GpsModulesPage'))
const TripPlaybackPage   = React.lazy(() => import('./pages/TripPlaybackPage'))
const AdminPendingStudentsPage = React.lazy(() => import('./pages/admin/AdminPendingStudentsPage'))
const AdminAddressRequestsPage = React.lazy(() => import('./pages/admin/AdminAddressRequestsPage'))
const PlanningPage       = React.lazy(() => import('./pages/admin/PlanningPage'))
const AdminModulesPage   = React.lazy(() => import('./pages/admin/AdminModulesPage'))
const ParentTrackingPage = React.lazy(() => import('./pages/parent/ParentTrackingPage'))
const ParentChildrenPage = React.lazy(() => import('./pages/parent/ParentChildrenPage'))
const ParentDashboardPage = React.lazy(() => import('./pages/parent/ParentDashboardPage'))
const ParentSchedulePage = React.lazy(() => import('./pages/parent/ParentSchedulePage'))
const ParentRequestsPage = React.lazy(() => import('./pages/parent/ParentRequestsPage'))
const ParentAlertsPage   = React.lazy(() => import('./pages/parent/ParentAlertsPage'))
const ParentProfilePage  = React.lazy(() => import('./pages/parent/ParentProfilePage'))
const DemoPage           = React.lazy(() => import('./pages/DemoPage'))
const AccountPage        = React.lazy(() => import('./pages/AccountPage'))
const RoutePlannerPage   = React.lazy(() => import('./pages/RoutePlannerPage'))
const DriverSimulationPage = React.lazy(() => import('./pages/DriverSimulationPage'))
const DriverDashboardPage = React.lazy(() => import('./pages/DriverDashboardPage'))
const DriverRunPage      = React.lazy(() => import('./pages/DriverRunPage'))
const DriverLivePage     = React.lazy(() => import('./pages/DriverLivePage'))
const DriverInspectionPage = React.lazy(() => import('./pages/DriverInspectionPage'))
const TripHistoryPage    = React.lazy(() => import('./pages/TripHistoryPage'))
const TripAssignmentPage = React.lazy(() => import('./pages/TripAssignmentPage'))
const BusesPage          = React.lazy(() => import('./pages/BusesPage'))
const SuperAdminPage     = React.lazy(() => import('./pages/admin/SuperAdminPage'))

function RoleIndexRedirect() {
  const { user } = useSelector((s: RootState) => s.auth)
  const dest =
    user?.role === 'PARENT' ? '/parent'
    : user?.role === 'DRIVER' ? '/driver'
    : user?.role === 'SUPER_ADMIN' ? '/super-admin'
    : '/dashboard'
  return <Navigate to={dest} replace />
}

const ProtectedRoute = ({
  children,
  roles,
}: {
  children: React.ReactNode
  roles?: string[]
}) => {
  const { isAuthenticated, user } = useSelector((s: RootState) => s.auth)
  if (!isAuthenticated) return <Navigate to="/accueil" replace />
  if (roles && user?.role && !roles.includes(user.role)) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

export default function App() {
  useIsAuthenticated()
  const dispatch = useDispatch()
  const { token, isAuthenticated, sessionRestored } = useSelector(
    (s: RootState) => s.auth,
  )

  useEffect(() => {
    if (token && !isAuthenticated) {
      api
        .get('/auth/me')
        .then(r => dispatch(loginSuccess({ user: r.data, token })))
        .catch(() => dispatch(logout()))
    }
  }, [])

  if (!sessionRestored) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center bg-gray-50">
          <LoadingSpinner />
        </div>
      }
    >
      <Routes>
        <Route path="/accueil" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/mfa"   element={<MfaPage />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<RoleIndexRedirect />} />
          <Route path="dashboard"       element={<DashboardPage />} />
          <Route path="tracking"        element={<TrackingPage />} />
          <Route path="alerts"          element={<AlertsPage />} />
          <Route path="circuits"        element={<CircuitsPage />} />
          <Route path="students"        element={<StudentsPage />} />
          <Route path="parent"          element={<ParentDashboardPage />} />
          <Route path="parent/tracking" element={<ParentTrackingPage />} />
          <Route path="parent/children" element={<ParentChildrenPage />} />
          <Route path="parent/schedule" element={<ParentSchedulePage />} />
          <Route path="parent/requests" element={<ParentRequestsPage />} />
          <Route path="parent/alerts"   element={<ParentAlertsPage />} />
          <Route path="parent/profile"  element={<ParentProfilePage />} />
          <Route path="demo"            element={<DemoPage />} />
          <Route
            path="driver-simulation"
            element={
              <ProtectedRoute roles={['ADMIN', 'DRIVER', 'DISPATCHER']}>
                <DriverSimulationPage />
              </ProtectedRoute>
            }
          />
          <Route path="account"         element={<AccountPage />} />
          <Route
            path="driver"
            element={
              <ProtectedRoute roles={['ADMIN', 'DRIVER']}>
                <DriverDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="driver/run/:id"
            element={
              <ProtectedRoute roles={['ADMIN', 'DRIVER', 'DISPATCHER']}>
                <DriverRunPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="driver/live"
            element={
              <ProtectedRoute roles={['ADMIN', 'DRIVER', 'DISPATCHER']}>
                <DriverLivePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="driver/inspection"
            element={
              <ProtectedRoute roles={['ADMIN', 'DRIVER', 'DISPATCHER']}>
                <DriverInspectionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="trips"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER', 'DISPATCHER', 'DRIVER']}>
                <TripHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="trips/assign"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER', 'DISPATCHER']}>
                <TripAssignmentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="buses"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER', 'DISPATCHER']}>
                <BusesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="routes"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER', 'DISPATCHER']}>
                <RoutePlannerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="super-admin"
            element={
              <ProtectedRoute roles={['SUPER_ADMIN']}>
                <SuperAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="admin"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/roles"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminRolesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/settings"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/import"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER']}>
                <AdminImportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/classes"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER']}>
                <SchoolClassesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/zones"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER']}>
                <SafetyZonesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/service-zones"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER']}>
                <ServiceZonesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/absences"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER', 'DISPATCHER']}>
                <AdminAbsencesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/gps-modules"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER']}>
                <GpsModulesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="playback"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER', 'DISPATCHER']}>
                <TripPlaybackPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/pending-students"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER']}>
                <AdminPendingStudentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/address-requests"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER', 'DISPATCHER']}>
                <AdminAddressRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="planning"
            element={
              <ProtectedRoute roles={['ADMIN', 'MANAGER', 'DISPATCHER']}>
                <PlanningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/modules"
            element={
              <ProtectedRoute roles={['ADMIN']}>
                <AdminModulesPage />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
